import { useMemo } from "react";
import { Badge } from "./ui/Badge";

export interface ModalidadConfigRow {
  id: number;
  nombre: string;
  tipo: string;
  precio_minimo: number;
  activo: boolean;
  precioVentaInput: string;
}

function tipoLabel(tipo: string): string {
  if (tipo === "diario") return "Diario";
  if (tipo === "anual") return "Anual";
  return tipo;
}

function ModalidadTableSection({
  title,
  rows,
  checkLabel,
  onChange,
}: {
  title: string;
  rows: ModalidadConfigRow[];
  checkLabel: string;
  onChange: (id: number, field: "activo" | "precioVenta", value: boolean | string) => void;
}) {
  if (rows.length === 0) return null;

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Modalidad
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Tipo
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Precio base CC
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Mi precio
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                {checkLabel}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rows.map((row) => (
              <tr key={row.id} className={row.activo ? "" : "opacity-60"}>
                <td className="px-4 py-3 text-sm font-medium text-[#2C2C2C]">{row.nombre}</td>
                <td className="px-4 py-3 text-sm">
                  <Badge variant={row.tipo === "anual" ? "success" : "neutral"}>
                    {tipoLabel(row.tipo)}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right text-sm text-gray-700">
                  {row.precio_minimo.toFixed(2)} €
                </td>
                <td className="px-4 py-3 text-right">
                  <input
                    type="number"
                    min={row.precio_minimo}
                    step="0.01"
                    placeholder={`${row.precio_minimo.toFixed(2)}€`}
                    value={row.precioVentaInput}
                    onChange={(e) => onChange(row.id, "precioVenta", e.target.value)}
                    className="w-28 rounded border border-gray-300 px-2 py-1.5 text-sm text-right focus:border-[#1A3AB8] focus:outline-none focus:ring-1 focus:ring-[#1A3AB8]"
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={row.activo}
                    onChange={(e) => onChange(row.id, "activo", e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-[#1A3AB8] focus:ring-[#1A3AB8]"
                    aria-label={`${checkLabel} ${row.nombre}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface ModalidadConfigTableProps {
  modalidades: ModalidadConfigRow[];
  onChange: (id: number, field: "activo" | "precioVenta", value: boolean | string) => void;
  checkLabel?: string;
}

export function ModalidadConfigTable({
  modalidades,
  onChange,
  checkLabel = "Activo",
}: ModalidadConfigTableProps) {
  const diarias = useMemo(() => modalidades.filter((r) => r.tipo === "diario"), [modalidades]);
  const anuales = useMemo(() => modalidades.filter((r) => r.tipo === "anual"), [modalidades]);

  return (
    <div className="space-y-8">
      <ModalidadTableSection
        title="Diario"
        rows={diarias}
        checkLabel={checkLabel}
        onChange={onChange}
      />
      <ModalidadTableSection
        title="Anual"
        rows={anuales}
        checkLabel={checkLabel}
        onChange={onChange}
      />
    </div>
  );
}
